import { __decorate, __metadata } from 'tslib';
import * as i0 from '@angular/core';
import { Directive, HostListener, booleanAttribute, Component, ViewEncapsulation, ChangeDetectionStrategy, Input, inject, NgModule } from '@angular/core';
import * as i1 from '@odx/angular/cdk/a11y';
import { ListFocusManagerOptionDirective, ListFocusManagerDirective } from '@odx/angular/cdk/a11y';
import * as i1$1 from '@odx/angular/components/dropdown';
import { closeCurrentDropdown, DropdownDirective } from '@odx/angular/components/dropdown';
import { CSSComponent, CSSModifier } from '@odx/angular/internal';
import { injectElement, deferFn, hasChanged } from '@odx/angular/utils';
import { CommonModule } from '@angular/common';
import { ActionGroupComponent } from '@odx/angular/components/action-group';
import { CoreModule } from '@odx/angular';
import { IconComponent } from '@odx/angular/components/icon';

/**
 * Represents a directive for a menu item.
 * Uses the `ListFocusManagerOptionDirective` to manage focus within the menu items.
 *
 * @see {ListFocusManagerOptionDirective}
 */
let MenuItemDirective = class MenuItemDirective {
    constructor() {
        this.element = injectElement();
    }
    selectItem() {
        deferFn(() => closeCurrentDropdown(this.element.nativeElement));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuItemDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: MenuItemDirective, isStandalone: true, selector: "a[odxButton][odxMenuItem], button[odxButton][odxMenuItem]", host: { attributes: { "role": "menuitem" }, listeners: { "click": "selectItem()" } }, hostDirectives: [{ directive: i1.ListFocusManagerOptionDirective }], ngImport: i0 }); }
};
MenuItemDirective = __decorate([
    CSSComponent('menu-item')
], MenuItemDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'a[odxButton][odxMenuItem], button[odxButton][odxMenuItem]',
                    standalone: true,
                    hostDirectives: [ListFocusManagerOptionDirective],
                    host: {
                        role: 'menuitem',
                    },
                }]
        }], propDecorators: { selectItem: [{
                type: HostListener,
                args: ['click']
            }] } });

/**
 * `MenuComponent` provides an Angular component for creating a navigational menu structure in an application.
 * It utilizes the `ListFocusManagerDirective` to manage focus within the menu items and integrates with other custom components like `ActionGroupComponent`.
 * The component is styled using CSS component techniques to ensure a consistent look and feel across different parts of the application.
 * @see {ListFocusManagerDirective}
 */
let MenuComponent = class MenuComponent {
    constructor() {
        this.element = injectElement();
        /**
         * A CSS modifier that indicates if the menu should use a tile layout. This is controlled through an input property which can be bound to a boolean value.
         *
         * @type {boolean}
         * @default false
         */
        this.tile = false;
    }
    onTab(event) {
        event.preventDefault();
        event.stopPropagation();
        closeCurrentDropdown(this.element.nativeElement);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: MenuComponent, isStandalone: true, selector: "odx-menu", inputs: { tile: ["tile", "tile", booleanAttribute] }, host: { attributes: { "role": "menu" }, listeners: { "keydown.tab": "onTab($event)" } }, hostDirectives: [{ directive: i1.ListFocusManagerDirective }], ngImport: i0, template: "@if (tile) {\n  <div class=\"odx-menu__title\">\n    <ng-content />\n  </div>\n}\n<odx-action-group class=\"odx-menu__inner\">\n  <ng-content select=\"[odxButton]\" ngProjectAs=\"[odxButton]\" />\n</odx-action-group>\n", dependencies: [{ kind: "component", type: ActionGroupComponent, selector: "odx-action-group", inputs: ["reverse"] }, { kind: "ngmodule", type: CommonModule }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
__decorate([
    CSSModifier(),
    __metadata("design:type", Object)
], MenuComponent.prototype, "tile", void 0);
MenuComponent = __decorate([
    CSSComponent('menu')
], MenuComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'odx-menu', standalone: true, imports: [ActionGroupComponent, CommonModule], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, hostDirectives: [ListFocusManagerDirective], host: {
                        role: 'menu',
                    }, template: "@if (tile) {\n  <div class=\"odx-menu__title\">\n    <ng-content />\n  </div>\n}\n<odx-action-group class=\"odx-menu__inner\">\n  <ng-content select=\"[odxButton]\" ngProjectAs=\"[odxButton]\" />\n</odx-action-group>\n" }]
        }], propDecorators: { tile: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], onTab: [{
                type: HostListener,
                args: ['keydown.tab', ['$event']]
            }] } });

const DEFAULT_MENU_DROPDOWN_OPTIONS = {
    containerClass: 'odx-menu-overlay',
    enableFallback: true,
};
/**
 * `MenuDirective` integrates a menu system into elements using the `DropdownDirective`
 * for dynamic dropdown functionality.
 * It enables flexible positioning and content injection capabilities, making it suitable
 * for creating context menus, dropdown lists, and other interactive menu-driven components.
 *
 * @see {DropdownDirective}
 */
let MenuDirective = class MenuDirective {
    constructor() {
        this.dropdown = inject(DropdownDirective, { host: true });
        this.element = injectElement();
    }
    ngOnChanges(changes) {
        if (hasChanged(changes, 'position', false)) {
            this.dropdown.options = {
                ...DEFAULT_MENU_DROPDOWN_OPTIONS,
                position: this.position ?? undefined,
            };
        }
    }
    /**
     * Returns whether the dropdown is currently open.
     *
     * @returns {boolean} `true` if the dropdown is open, `false` otherwise.
     */
    isOpen() {
        return this.dropdown.isOpen;
    }
    /**
     * Closes the dropdown.
     *
     * @param {Event} [event] The event that triggered the dropdown close.
     */
    close(event) {
        this.dropdown.close(event);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: MenuDirective, isStandalone: true, selector: "[odxMenu]", inputs: { content: ["odxMenu", "content"], position: ["odxMenuPosition", "position"] }, exportAs: ["odxMenu"], usesOnChanges: true, hostDirectives: [{ directive: i1$1.DropdownDirective, inputs: ["odxDropdown", "odxMenu"] }], ngImport: i0 }); }
};
MenuDirective = __decorate([
    CSSComponent('menu-host')
], MenuDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[odxMenu]',
                    exportAs: 'odxMenu',
                    standalone: true,
                    hostDirectives: [
                        {
                            directive: DropdownDirective,
                            inputs: ['odxDropdown:odxMenu'],
                        },
                    ],
                }]
        }], propDecorators: { content: [{
                type: Input,
                args: ['odxMenu']
            }], position: [{
                type: Input,
                args: ['odxMenuPosition']
            }] } });

const modules = [MenuComponent, MenuDirective, MenuItemDirective, IconComponent];
class MenuModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: MenuModule, imports: [MenuComponent, MenuDirective, MenuItemDirective, IconComponent], exports: [CoreModule, MenuComponent, MenuDirective, MenuItemDirective, IconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuModule, imports: [MenuComponent, IconComponent, CoreModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MenuModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [modules],
                    exports: [CoreModule, ...modules],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { MenuComponent, MenuDirective, MenuItemDirective, MenuModule };
